import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  IconButton,
  Icon,
  Text,
} from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { SampleBreakdown } from "./makeSampleBreakdown";
import { GraphColor } from "@tether-web-portal/types/Room";
import { OutsideWithReading } from "@tether-web-portal/types/OutsideWithReading";
import { LocationType, QueryKeys, useUpdateLocationMutation } from "@tether-web-portal/api/properties/hooks";
import { RoomLocationWithReading } from "@tether-web-portal/types/RoomLocation";
import { useQueryClient } from "react-query";
import { ChevronLeftIcon } from "@chakra-ui/icons";
import { RoomLocationEditableHeader, DetailsView, LiveCountView } from "./room-location-modal";
import { DeviceType } from "@tether-web-portal/types/DeviceType";
import { useChildRoomLocationsMap } from "@tether-web-portal/components/areas/location-reading/room-location-modal/useChildRoomLocations";
import { ChildLocationForm } from "@tether-web-portal/components/areas/location-reading/room-location-modal/ChildLocationForm";

enum RoomLocationModalViewState {
  DETAILS = "DETAILS",
  LIVE_COUNTS = "LIVE_COUNTS",
  ROOM_RADAR_REGIONS = "ROOM_RADAR_REGIONS",
  ROOM_RADAR_LINES = "ROOM_RADAR_LINES",
}

interface RoomLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  roomLocation: RoomLocationWithReading | OutsideWithReading;
  locationType: "ROOMLOCATION" | "OUTSIDE";
  propertyId: string;
  breakdown?: SampleBreakdown;
  editMode?: boolean;
  hasDevice: boolean;
}

export const RoomLocationModal = ({
  isOpen,
  roomLocation,
  locationType,
  onClose,
  breakdown,
  propertyId,
  editMode,
  hasDevice,
}: RoomLocationModalProps) => {
  const [viewState, setViewState] = useState<RoomLocationModalViewState>(RoomLocationModalViewState.DETAILS);

  const queryClient = useQueryClient();

  const updateLocationmutation = useUpdateLocationMutation(
    locationType === "OUTSIDE" ? LocationType.Outside : LocationType.RoomLocation,
  );

  const onChangeLocation = useCallback(
    async (id: string, input: Partial<RoomLocationWithReading | OutsideWithReading>) => {
      await updateLocationmutation.mutateAsync({
        propertyId,
        input: {
          ...input,
          id,
        },
      });
      await queryClient.invalidateQueries([QueryKeys.PROPERTY, propertyId]);
      await queryClient.invalidateQueries([QueryKeys.PROPERTY_READINGS, propertyId]);
    },
    [updateLocationmutation, queryClient, propertyId],
  );

  const childRoomLocationMap = useChildRoomLocationsMap(roomLocation);

  return (
    <>
      <Modal size="xl" blockScrollOnMount={false} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />

        <ModalContent p={"12px"}>
          <Flex alignItems="flex-start" gap={2}>
            {viewState !== RoomLocationModalViewState.DETAILS && (
              <IconButton
                aria-label="Back"
                icon={<Icon boxSize={6} as={ChevronLeftIcon} />}
                onClick={() => setViewState(RoomLocationModalViewState.DETAILS)}
                variant="ghost"
                size="md"
              />
            )}

            <Flex alignItems={"center"} minHeight={"40px"} width="100%">
              {viewState === RoomLocationModalViewState.DETAILS && (
                <RoomLocationEditableHeader
                  editMode={editMode}
                  breakdown={breakdown}
                  roomLocation={roomLocation}
                  onLiveCountsClick={() => setViewState(RoomLocationModalViewState.LIVE_COUNTS)}
                  onChangeLocation={onChangeLocation}
                />
              )}
              {viewState === RoomLocationModalViewState.LIVE_COUNTS && (
                <Text fontSize="xl" fontWeight={"bold"}>
                  Room Radar Live Counts
                </Text>
              )}
            </Flex>

            <Box ml="auto">
              <ModalCloseButton position="relative" top={0} right={0} />
            </Box>
          </Flex>

          <ModalBody display="flex" pt="8px" flexDirection="column" alignItems="center">
            {viewState === RoomLocationModalViewState.DETAILS && (
              <DetailsView
                roomLocation={roomLocation}
                breakdown={breakdown}
                locationType={locationType}
                hasDevice={hasDevice}
                propertyId={propertyId}
                childRoomLocationMap={childRoomLocationMap}
                onChildDeviceTypeClick={(deviceType: DeviceType) => {
                  if (deviceType === DeviceType.ROOM_RADAR_LINE)
                    return setViewState(RoomLocationModalViewState.ROOM_RADAR_LINES);
                  if (deviceType === DeviceType.ROOM_RADAR_REGION)
                    return setViewState(RoomLocationModalViewState.ROOM_RADAR_REGIONS);
                }}
              />
            )}
            {viewState === RoomLocationModalViewState.ROOM_RADAR_LINES && (
              <ChildLocationForm
                title="Entry/Exit Lines"
                subtitle="These lines track people entering or leaving this area"
                childLocations={childRoomLocationMap[DeviceType.ROOM_RADAR_LINE]}
                indexLabel="Line"
                onChangeLocation={onChangeLocation}
              />
            )}
            {viewState === RoomLocationModalViewState.ROOM_RADAR_REGIONS && (
              <ChildLocationForm
                title="Regions"
                subtitle="Specific spaces within this area that are tracking people"
                childLocations={childRoomLocationMap[DeviceType.ROOM_RADAR_REGION]}
                indexLabel="Region"
                onChangeLocation={onChangeLocation}
              />
            )}
            {viewState === RoomLocationModalViewState.LIVE_COUNTS && (
              <LiveCountView roomLocationId={roomLocation.id} />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
